import { useApolloClient } from "@apollo/client";
import { getDataOrThrow } from "@msys/common";
import { ModalOpenButton } from "@msys/ui";
import { Add as AddIcon } from "@mui/icons-material";
import { Edit as EditIcon } from "@mui/icons-material";
import { Alert, Button, Divider, IconButton, Stack } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { useNavigate } from "react-router-dom";
import {
  DocActorModifyInput,
  DocActorType,
  namedOperations,
} from "../../../../clients/graphqlTypes.js";
import { RestrictedByDocumentPermissionWithDebug } from "../../../auth/RestrictedByDocumentPermission.js";
import { CrmCompanySelectModal } from "../../crm-companies/modals/CrmCompanySelectModal.js";
import { DocumentActorData } from "../../documents/DocumentActorData.js";
import { DocumentActorEditModal } from "../../documents/DocumentActorEditModal.js";
import { DocumentActorsModal } from "../../documents/DocumentActorsModal.js";
import { useActorTypes } from "../../documents/useActorTypes.js";
import { useDuplicateQuote } from "../hooks/useDuplicateQuote.js";
import {
  useAddQuoteDocumentActorMutation,
  useModifyQuoteDocumentActorMutation,
  useQuoteDocumentActorsSuspenseQuery,
  useResetQuoteDocActorMutation,
} from "./QuoteDocumentActorsModal.generated.js";

interface Props {
  handleClose: () => void;
  projectId: string;
  quoteId: string;
}

export const QuoteDocumentActorsModal = ({
  handleClose,
  projectId,
  quoteId,
}: Props) => {
  const { t } = useTranslate(["Global", "Quote"]);
  const { actorTypeLabels } = useActorTypes();
  const navigate = useNavigate();

  const client = useApolloClient();
  const query = useQuoteDocumentActorsSuspenseQuery({
    client,
    variables: { projectId, quoteId },
  });
  const [addActor] = useAddQuoteDocumentActorMutation({
    client,
    refetchQueries: [namedOperations.Query.QuoteDocumentActors],
    awaitRefetchQueries: true,
  });
  const [modifyActor] = useModifyQuoteDocumentActorMutation({
    client,
    refetchQueries: [namedOperations.Query.QuoteDocumentActors],
    awaitRefetchQueries: true,
  });
  const [resetActor] = useResetQuoteDocActorMutation({
    client,
    refetchQueries: [namedOperations.Query.QuoteDocumentActors],
    awaitRefetchQueries: true,
  });
  const { duplicate, loading: duplicateLoading } = useDuplicateQuote({
    projectId,
    quoteId,
  });

  const quote = getDataOrThrow(query.data!.quote).quote; // FIXME: why is data possibly undefined?
  if (!quote) throw new Error("Quote not found");

  const canEdit = quote.canModifyActors;

  const contractorActor = quote?.contractor;
  const contracteeActor = quote?.contractee;
  const clientActor = quote?.client;

  const handleAdd = async (actorType: DocActorType, crmCompanyId: string) => {
    await addActor({
      variables: {
        input: {
          projectId,
          docId: quoteId,
          docActorType: actorType,
          crmCompanyId,
        },
      },
    });
  };

  const handleChange = async (
    actorId: string,
    actorType: DocActorType,
    actorInput: DocActorModifyInput
  ) => {
    await modifyActor({
      variables: {
        input: {
          projectId,
          docId: quoteId,
          docActorId: actorId,
          docActorType: actorType,
          docActor: actorInput,
        },
      },
    });
  };

  const handleDuplicate = async () => {
    const newQuoteId = await duplicate();
    if (!newQuoteId) return;

    handleClose();
    navigate(`/projects/${projectId}/quotes/${newQuoteId}`);
  };

  const handleReset = async (
    actorId: string,
    actorType: DocActorType,
    crmCompanyId: string
  ) => {
    await resetActor({
      variables: {
        input: {
          projectId,
          docId: quoteId,
          docActorType: actorType,
          docActorId: actorId,
          crmCompanyId,
        },
      },
    });
  };

  return (
    <DocumentActorsModal handleClose={handleClose}>
      {!canEdit && (
        <Alert
          severity="info"
          action={
            <RestrictedByDocumentPermissionWithDebug
              permission="quote_document_update"
              document={quote}
            >
              <Button
                color="inherit"
                size="small"
                onClick={handleDuplicate}
                disabled={duplicateLoading}
              >
                {t("Duplicate quote", { ns: "Quote" })}
              </Button>
            </RestrictedByDocumentPermissionWithDebug>
          }
        >
          {t(
            "The relevant parties cannot be changed because this quote has already been published, decided or shared. Duplicate the quote to continue with a separate copy.",
            { ns: "Quote" }
          )}
        </Alert>
      )}
      <Stack
        direction={"row"}
        spacing={2}
        sx={{
          alignItems: "flex-start",
          justifyContent: "space-between",
        }}
      >
        <DocumentActorData actorType={"CONTRACTOR"} actor={contractorActor} />
        {canEdit &&
          (contractorActor ? (
            <RestrictedByDocumentPermissionWithDebug
              permission="quote_actors_update"
              document={quote}
            >
              <ModalOpenButton
                Modal={DocumentActorEditModal}
                modalProps={{
                  actor: contractorActor,
                  title: t("{docActorType} details", {
                    ns: "Global",
                    docActorType: actorTypeLabels[contractorActor.type],
                  }),
                  handleReset: async (crmCompanyId, handleClose) => {
                    await handleReset(
                      contractorActor.id,
                      contractorActor.type,
                      crmCompanyId
                    );
                    handleClose();
                  },
                  handleComplete: async (actorFormValues, handleClose) => {
                    await handleChange(
                      contractorActor.id,
                      contractorActor.type,
                      actorFormValues
                    );
                    handleClose();
                  },
                }}
              >
                <IconButton
                  color="secondary"
                  aria-label={t("{docActorType} details", {
                    ns: "Global",
                    docActorType: actorTypeLabels[contractorActor.type],
                  })}
                >
                  <EditIcon />
                </IconButton>
              </ModalOpenButton>
            </RestrictedByDocumentPermissionWithDebug>
          ) : (
            <RestrictedByDocumentPermissionWithDebug
              permission="quote_actors_update"
              document={quote}
            >
              <ModalOpenButton
                Modal={CrmCompanySelectModal}
                modalProps={{
                  title: t("Select {docActorType}", {
                    ns: "Global",
                    docActorType: actorTypeLabels["CONTRACTOR"],
                  }),
                  inputLabel: t("Choose one of your contacts", {
                    ns: "Global",
                  }),
                  canCreateNew: true,
                  createNewLabel: t("Create new contact", { ns: "Global" }),
                  allowedContactTypesForCreate: ["COMPANY", "INDIVIDUAL"],
                  required: true,
                  handleComplete: async (crmCompanyId, handleClose) => {
                    await handleAdd("CONTRACTOR", crmCompanyId);
                    handleClose();
                  },
                }}
              >
                <IconButton color="secondary">
                  <AddIcon />
                </IconButton>
              </ModalOpenButton>
            </RestrictedByDocumentPermissionWithDebug>
          ))}
      </Stack>
      <Divider />
      <Stack
        direction={"row"}
        spacing={2}
        sx={{
          alignItems: "flex-start",
          justifyContent: "space-between",
        }}
      >
        <DocumentActorData actorType={"CONTRACTEE"} actor={contracteeActor} />
        {canEdit &&
          (contracteeActor ? (
            <RestrictedByDocumentPermissionWithDebug
              permission="quote_actors_update"
              document={quote}
            >
              <ModalOpenButton
                Modal={DocumentActorEditModal}
                modalProps={{
                  actor: contracteeActor,
                  title: t("{docActorType} details", {
                    ns: "Global",
                    docActorType: actorTypeLabels[contracteeActor.type],
                  }),
                  handleReset: async (crmCompanyId, handleClose) => {
                    await handleReset(
                      contracteeActor.id,
                      contracteeActor.type,
                      crmCompanyId
                    );
                    handleClose();
                  },
                  handleComplete: async (actorFormValues, handleClose) => {
                    await handleChange(
                      contracteeActor.id,
                      contracteeActor.type,
                      actorFormValues
                    );
                    handleClose();
                  },
                }}
              >
                <IconButton
                  color="secondary"
                  aria-label={t("{docActorType} details", {
                    ns: "Global",
                    docActorType: actorTypeLabels[contracteeActor.type],
                  })}
                >
                  <EditIcon />
                </IconButton>
              </ModalOpenButton>
            </RestrictedByDocumentPermissionWithDebug>
          ) : (
            <RestrictedByDocumentPermissionWithDebug
              permission="quote_actors_update"
              document={quote}
            >
              <ModalOpenButton
                Modal={CrmCompanySelectModal}
                modalProps={{
                  title: t("Select {docActorType}", {
                    ns: "Global",
                    docActorType: actorTypeLabels["CONTRACTEE"],
                  }),
                  inputLabel: t("Choose one of your contacts", {
                    ns: "Global",
                  }),
                  canCreateNew: true,
                  createNewLabel: t("Create new contact", { ns: "Global" }),
                  allowedContactTypesForCreate: ["COMPANY", "INDIVIDUAL"],
                  required: true,
                  handleComplete: async (crmCompanyId, handleClose) => {
                    await handleAdd("CONTRACTEE", crmCompanyId);
                    handleClose();
                  },
                }}
              >
                <IconButton color="secondary">
                  <AddIcon />
                </IconButton>
              </ModalOpenButton>
            </RestrictedByDocumentPermissionWithDebug>
          ))}
      </Stack>
      <Divider />
      <Stack
        direction={"row"}
        spacing={2}
        sx={{
          alignItems: "flex-start",
          justifyContent: "space-between",
        }}
      >
        <DocumentActorData actorType={"CLIENT"} actor={clientActor} />
        {canEdit &&
          (clientActor ? (
            <RestrictedByDocumentPermissionWithDebug
              permission="quote_actors_update"
              document={quote}
            >
              <ModalOpenButton
                Modal={DocumentActorEditModal}
                modalProps={{
                  actor: clientActor,
                  title: t("{docActorType} details", {
                    ns: "Global",
                    docActorType: actorTypeLabels[clientActor.type],
                  }),
                  handleReset: async (crmCompanyId, handleClose) => {
                    await handleReset(
                      clientActor.id,
                      clientActor.type,
                      crmCompanyId
                    );
                    handleClose();
                  },
                  handleComplete: async (actorFormValues, handleClose) => {
                    await handleChange(
                      clientActor.id,
                      clientActor.type,
                      actorFormValues
                    );
                    handleClose();
                  },
                }}
              >
                <IconButton
                  color="secondary"
                  aria-label={t("{docActorType} details", {
                    ns: "Global",
                    docActorType: actorTypeLabels[clientActor.type],
                  })}
                >
                  <EditIcon />
                </IconButton>
              </ModalOpenButton>
            </RestrictedByDocumentPermissionWithDebug>
          ) : (
            <RestrictedByDocumentPermissionWithDebug
              permission="quote_actors_update"
              document={quote}
            >
              <ModalOpenButton
                Modal={CrmCompanySelectModal}
                modalProps={{
                  title: t("Select {docActorType}", {
                    ns: "Global",
                    docActorType: actorTypeLabels["CLIENT"],
                  }),
                  inputLabel: t("Choose one of your contacts", {
                    ns: "Global",
                  }),
                  canCreateNew: true,
                  createNewLabel: t("Create new contact", { ns: "Global" }),
                  allowedContactTypesForCreate: ["COMPANY", "INDIVIDUAL"],
                  required: true,
                  handleComplete: async (crmCompanyId, handleClose) => {
                    await handleAdd("CLIENT", crmCompanyId);
                    handleClose();
                  },
                }}
              >
                <IconButton
                  color="secondary"
                  aria-label={t("Select {docActorType}", {
                    ns: "Global",
                    docActorType: actorTypeLabels["CLIENT"],
                  })}
                >
                  <AddIcon />
                </IconButton>
              </ModalOpenButton>
            </RestrictedByDocumentPermissionWithDebug>
          ))}
      </Stack>
    </DocumentActorsModal>
  );
};
